import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { apiKeyService } from '../../../services/apiKeyService';

const ApiKeyDetailsModal = ({ isOpen, onClose, apiKey, onUpdateKey, onDeleteKey }) => {
  const [usageData, setUsageData] = useState([]);
  const [usageStats, setUsageStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isOpen && apiKey?.id) {
      loadUsageData();
      loadUsageStats();
    }
  }, [isOpen, apiKey?.id]);

  const loadUsageData = async () => {
    if (!apiKey?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await apiKeyService?.getApiKeyUsage(apiKey?.id, { limit: 100 });
      
      if (error) {
        setError(error);
      } else {
        setUsageData(data || []);
      }
    } catch (err) {
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsageStats = async () => {
    if (!apiKey?.id) return;
    
    try {
      const { data, error } = await apiKeyService?.getUsageStatistics(apiKey?.id);
      
      if (error) {
        setError(error);
      } else {
        setUsageStats(data);
      }
    } catch (err) {
      setError('Failed to load usage statistics');
    }
  };

  const handleRegenerateKey = async () => {
    if (!confirm('Are you sure you want to regenerate this API key? The current key will be revoked immediately.')) {
      return;
    }

    try {
      const { error } = await apiKeyService?.regenerateApiKey(apiKey?.id);
      
      if (error) {
        setError(error);
        return;
      }

      onUpdateKey?.();
      onClose?.();
    } catch (err) {
      setError('Failed to regenerate API key');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'inactive':
        return 'text-yellow-600 bg-yellow-100';
      case 'revoked':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusCodeColor = (code) => {
    if (code >= 200 && code < 300) return 'text-green-600';
    if (code >= 400 && code < 500) return 'text-orange-600';
    if (code >= 500) return 'text-red-600';
    return 'text-gray-600';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white border border-gray-200 rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{apiKey?.name}</h2>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(apiKey?.status)}`}>
                {apiKey?.status?.charAt(0)?.toUpperCase() + apiKey?.status?.slice(1)}
              </span>
              <span className="text-sm text-gray-500">•</span>
              <span className="text-sm text-gray-600 capitalize">{apiKey?.type} Key</span>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="flex items-center"
          >
            <span>✗</span>
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'usage', label: 'Usage History' },
              { id: 'settings', label: 'Settings' }
            ]?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab?.id
                    ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab?.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <span className="text-red-600 mt-0.5">⚠️</span>
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Key Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Key Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Created</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(apiKey?.created_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Last Used</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(apiKey?.last_used_at)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Type</p>
                      <p className="text-sm font-medium text-gray-900 capitalize">{apiKey?.type}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-gray-600">Rate Limit</p>
                      <p className="text-sm font-medium text-gray-900">{apiKey?.rate_limit?.toLocaleString()} requests/hour</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Expires</p>
                      <p className="text-sm font-medium text-gray-900">
                        {apiKey?.expires_at ? formatDate(apiKey?.expires_at) : 'Never'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Last IP</p>
                      <p className="text-sm font-medium text-gray-900">{apiKey?.last_used_ip || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Permissions */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
                <div className="flex flex-wrap gap-2">
                  {apiKey?.permissions?.map((permission) => (
                    <span key={permission} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full border border-blue-200">
                      {permission}
                    </span>
                  ))}
                  {apiKey?.permissions?.length === 0 && (
                    <p className="text-sm text-gray-500">No permissions assigned</p>
                  )}
                </div>
              </div>

              {/* Usage Statistics */}
              {usageStats && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Usage Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-blue-600">{usageStats?.totalRequests || 0}</p>
                      <p className="text-sm text-blue-800">Total Requests</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-green-600">{usageStats?.successfulRequests || 0}</p>
                      <p className="text-sm text-green-800">Successful</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-red-600">{usageStats?.errorRequests || 0}</p>
                      <p className="text-sm text-red-800">Errors</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4">
                      <p className="text-2xl font-bold text-purple-600">{usageStats?.avgResponseTime || 0}ms</p>
                      <p className="text-sm text-purple-800">Avg Response</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Usage History Tab */}
          {activeTab === 'usage' && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent API Usage</h3>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm text-gray-600">Loading usage data...</p>
                </div>
              ) : usageData?.length > 0 ? (
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response Time</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {usageData?.slice(0, 50)?.map((log) => (
                          <tr key={log?.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log?.endpoint}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-xs font-medium bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                {log?.method}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`text-sm font-medium ${getStatusCodeColor(log?.status_code)}`}>
                                {log?.status_code}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {log?.response_time_ms ? `${log?.response_time_ms}ms` : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(log?.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No usage data available</p>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Key Management</h3>
              
              <div className="space-y-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-base font-medium text-gray-900 mb-2">Regenerate API Key</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Generate a new API key with the same permissions. The current key will be immediately revoked.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleRegenerateKey}
                    className="text-orange-600 hover:bg-orange-50"
                    disabled={apiKey?.status === 'revoked'}
                  >
                    Regenerate Key
                  </Button>
                </div>

                <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                  <h4 className="text-base font-medium text-red-900 mb-2">Danger Zone</h4>
                  <p className="text-sm text-red-700 mb-4">
                    Once you delete an API key, all applications using this key will lose access immediately.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
                        onDeleteKey?.(apiKey?.id);
                        onClose?.();
                      }
                    }}
                    className="text-red-600 hover:bg-red-100"
                  >
                    Delete API Key
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiKeyDetailsModal;